import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import { bookingStatuses } from 'constants/bookings';
import userTypes from 'constants/userTypes';
import { affiliateBookingStatuses, CANCELLATION_INITIATOR } from '@a1/shared';
import { useGetEntityName } from 'hooks-ts/useGetEntity';
import handleError from 'utils-ts/handle-error';
import callAzureFunction from 'utils-ts/call-azure-function';
import { CustomDateTimePicker } from 'components-ts/pickers/DateTimePickers';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// Statuses that trigger the initiator question - affiliate bookings included.
//
// Did-not-attend is deliberately out of scope for now: moving into it still applies the DNA policy
// charges backend-side, but staff are not asked who caused it, so those bookings bill exactly as
// they did before this feature. Re-add the two DidNotAttend values here to switch the prompt back
// on - DID_NOT_ATTEND_STATUSES and PROMPT_COPY.didNotAttend below are kept ready for that.
export const CANCELLATION_FAMILY_STATUSES = [bookingStatuses.Canceled.value, affiliateBookingStatuses.Canceled.value];

// Never matches while DNA is out of CANCELLATION_FAMILY_STATUSES, since the prompt only opens for
// statuses listed there. Kept, with the copy block below, so re-enabling the prompt is a one-line
// change to that list rather than a rewrite.
const DID_NOT_ATTEND_STATUSES = [bookingStatuses.DidNotAttend.value, affiliateBookingStatuses.DidNotAttend.value];

// The two statuses bill under different names - "Did Not Attend Charge" vs "Cancellation Charge"
// on the invoice - and nobody "requests" a no-show, so asking about one in the other's language
// would push staff towards the wrong answer.
const PROMPT_COPY = {
  cancellation: {
    title: 'Who initiated this cancellation?',
    respondent: 'requested it',
    practice: 'initiated it',
    chargedConsequence: 'Cancellation fees apply as normal',
    waivedConsequence: 'No cancellation fee will be charged',
    commentLabel: 'Reason for cancellation'
  },
  didNotAttend: {
    title: 'Why was this marked as not attended?',
    respondent: 'did not attend',
    practice: 'could not hold the appointment',
    chargedConsequence: 'Did not attend fees apply as normal',
    waivedConsequence: 'No did not attend fee will be charged',
    commentLabel: 'Reason for non-attendance'
  }
};

// Matches the column the comment lands in - shared/models/event-log.js stores payload as TEXT, but
// the row is only useful if the reason is readable at a glance in a dispute, not an essay.
const COMMENT_MAX_LENGTH = 500;

/**
 * Asks who initiated a cancellation when the answer would change what the respondent is billed.
 * Staff only: External Users are never asked.
 *
 * Shared by every dialog that can move a booking into a cancellation status, so the question is
 * asked the same way and only when it matters - the backend applies the policy on any status
 * change into the cancellation family, no matter which screen sent it.
 *
 * Cancellations only for now. The backend still charges did-not-attend bookings under the same
 * policy, but nobody is asked who caused those, so they keep their pre-feature billing - see
 * CANCELLATION_FAMILY_STATUSES.
 *
 * Staff must also record why, so a waived or charged fee can be explained later without asking
 * whoever clicked the button to remember. The answer and the comment settle together - a comment
 * without an initiator would not say which billing outcome it justifies.
 *
 * A respondent-initiated cancellation also records when the respondent asked, because the penalty
 * period is judged from that moment rather than from when staff got round to recording it.
 *
 * @returns {{
 *   resolveCancellationInitiator: (args: { bookingId: string|number, nextStatus: string,
 *     currentStatus: string, targetCompanyId?: string|number }) => Promise<{ proceed: boolean,
 *     cancellationInitiator?: string, cancellationComment?: string, cancellationRequestedAt?: string }>,
 *   cancellationInitiatorDialog: React.ReactNode
 * }}
 */
const useCancellationInitiatorPrompt = () => {
  const [open, setOpen] = useState(false);
  // Which wording the open dialog should use - set alongside `open` so the copy always matches
  // the status being applied.
  const [copy, setCopy] = useState(PROMPT_COPY.cancellation);
  // Now a two-step answer, so the choice has to be held rather than settling the promise on click.
  const [selectedInitiator, setSelectedInitiator] = useState(null);
  const [comment, setComment] = useState('');
  // When the respondent asked, in the booking location's timezone - the zone the penalty period
  // is judged in. The timezone comes from the gate response, so no call site has to supply it.
  const [requestedAt, setRequestedAt] = useState(null);
  const [bookingTimezone, setBookingTimezone] = useState('Etc/UTC');
  // Only after a failed Confirm, so nothing is already red the moment the dialog opens. One flag
  // for both halves: a Confirm that fails has failed as a whole, and flagging only the field the
  // staff member missed reads as the dialog noticing one omission at a time.
  const [confirmAttempted, setConfirmAttempted] = useState(false);
  const respondentEntityName = useGetEntityName('respondent');
  const companyName = useSelector((state) => state.appConfig?.data?.generalSettings?.companyName);
  const userType = useSelector((state) => state.user?.data?.type);

  // Held in a ref, not state: the click handlers need whatever resolver is current at click time,
  // and a re-render between opening the dialog and answering it must not lose the pending promise.
  const resolverRef = useRef(null);

  const trimmedComment = comment.trim();
  const isCommentMissing = trimmedComment.length === 0;
  const isInitiatorMissing = !selectedInitiator;
  const isRespondentInitiated = selectedInitiator === CANCELLATION_INITIATOR.RESPONDENT;

  const requestedAtError = isRespondentInitiated && !requestedAt?.isValid() ? 'Please enter when it was requested.' : null;

  const settle = useCallback((value) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(value);
  }, []);

  const handleConfirm = useCallback(() => {
    // Both halves are required, so a Confirm with either missing corrects the staff member
    // instead of silently recording a blank reason against a billing decision.
    if (isInitiatorMissing || isCommentMissing || requestedAtError) {
      setConfirmAttempted(true);
      return;
    }

    settle({
      cancellationInitiator: selectedInitiator,
      cancellationComment: trimmedComment,
      ...(isRespondentInitiated ? { cancellationRequestedAt: requestedAt.toISOString() } : {})
    });
  }, [
    selectedInitiator,
    isInitiatorMissing,
    isCommentMissing,
    requestedAtError,
    isRespondentInitiated,
    requestedAt,
    trimmedComment,
    settle
  ]);

  const resolveCancellationInitiator = useCallback(
    async ({ bookingId, nextStatus, currentStatus, targetCompanyId }) => {
      // Only a move INTO a cancellation status applies the policy, so editing a booking that is
      // already cancelled must not re-ask - and must not carry a stale answer that wipes charges.
      const isChangingToCancellationStatus = nextStatus !== currentStatus && CANCELLATION_FAMILY_STATUSES.includes(nextStatus);

      if (!isChangingToCancellationStatus || userType === userTypes.ExternalUser) {
        return { proceed: true };
      }

      let gate;

      try {
        const gateResponse = await callAzureFunction({
          url: `/bookings/${bookingId}/cancellation-initiator-required`,
          method: 'get',
          // Sent when the same submission also reassigns the company, so the gate is answered for
          // the company that will be billed rather than the one being replaced.
          ...(targetCompanyId ? { params: { targetCompanyId } } : {})
        });

        if (!gateResponse.data?.initiatorPromptRequired) {
          return { proceed: true };
        }

        gate = gateResponse.data;
      } catch (error) {
        // Fail open: a broken gate check should not block a status change staff are otherwise
        // allowed to make. Worst case is the existing (pre-feature) charge behaviour.
        handleError(error);
        return { proceed: true };
      }

      const answer = await new Promise((resolve) => {
        resolverRef.current = resolve;
        setCopy(DID_NOT_ATTEND_STATUSES.includes(nextStatus) ? PROMPT_COPY.didNotAttend : PROMPT_COPY.cancellation);
        // Cleared on open rather than on close, so a second cancellation never inherits the previous
        // booking's answer or reason.
        setSelectedInitiator(null);
        setComment('');
        setBookingTimezone(gate.timezone);
        setRequestedAt(dayjs().tz(gate.timezone));
        setConfirmAttempted(false);
        setOpen(true);
      });

      // Backing out of the question aborts the update rather than guessing an answer.
      if (!answer) {
        return { proceed: false };
      }

      return { proceed: true, ...answer };
    },
    [userType]
  );

  const cancellationInitiatorDialog = (
    /* No onClose: a backdrop click or escape press must not silently pick a billing outcome,
       so the dialog is dismissable only through its own buttons. */
    <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h3" id="cancellation-initiator-title">
          {copy.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Grouped so the error below is announced as belonging to the choice, not read as loose
            text after two unrelated buttons. */}
        <Stack
          spacing={1.5}
          sx={{ pt: 1 }}
          role="group"
          aria-labelledby="cancellation-initiator-title"
          aria-describedby={confirmAttempted && isInitiatorMissing ? 'cancellation-initiator-error' : undefined}
        >
          {[
            {
              initiator: CANCELLATION_INITIATOR.RESPONDENT,
              label: `The ${respondentEntityName.toLowerCase()} ${copy.respondent}`,
              consequence: copy.chargedConsequence
            },
            {
              initiator: CANCELLATION_INITIATOR.PRACTICE,
              label: `${companyName || 'We'} ${copy.practice}`,
              consequence: copy.waivedConsequence
            }
          ].map(({ initiator, label, consequence }) => (
            <Button
              key={initiator}
              // Filled once chosen: with the answer no longer submitting on click, the dialog has
              // to show which option is currently selected.
              variant={selectedInitiator === initiator ? 'contained' : 'outlined'}
              fullWidth
              onClick={() => setSelectedInitiator(initiator)}
              // Stacked and left-aligned rather than a row of equal buttons: these are not
              // symmetric choices, and the billing consequence needs room to be stated outright
              // instead of left for staff to infer.
              //
              // Plain boxes rather than nested Typography: the theme's outlined hover repaints the
              // button's own colour, which a Typography child would override and leave unreadable
              // against the filled background.
              sx={{
                textTransform: 'none',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                gap: 0.25,
                p: 2
              }}
            >
              <Box sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{label}</Box>
              <Box sx={{ fontSize: '0.8125rem', fontWeight: 400, opacity: 0.75 }}>{consequence}</Box>
            </Button>
          ))}

          {/* The choice is a pair of buttons rather than a form control, so it has no built-in
              error slot - without this, a Confirm blocked only by the missing initiator changes
              nothing on screen and the button reads as broken. Matches the TextField's helper
              text so both halves report a problem the same way. */}
          {confirmAttempted && isInitiatorMissing && (
            <Typography variant="caption" color="error" id="cancellation-initiator-error" sx={{ mt: -0.5 }}>
              Please choose who initiated this.
            </Typography>
          )}

          {isRespondentInitiated && (
            <CustomDateTimePicker
              label={`When did the ${respondentEntityName.toLowerCase()} request it? (${bookingTimezone})`}
              value={requestedAt}
              onChange={setRequestedAt}
              timezone={bookingTimezone}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: confirmAttempted && Boolean(requestedAtError),
                  helperText: confirmAttempted ? requestedAtError : undefined
                }
              }}
            />
          )}

          {/* Required: the initiator says which way the booking was billed, the comment says why,
              and only the second survives staff turnover. Stored on the WWevent log payload. */}
          <TextField
            required
            multiline
            minRows={2}
            fullWidth
            label={copy.commentLabel}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            error={confirmAttempted && isCommentMissing}
            helperText={confirmAttempted && isCommentMissing ? 'Please give a reason.' : `${trimmedComment.length}/${COMMENT_MAX_LENGTH}`}
            slotProps={{ htmlInput: { maxLength: COMMENT_MAX_LENGTH } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => settle(null)}>
          Cancel
        </Button>
        {/* Left enabled while incomplete so pressing it explains what is missing - a disabled
            button states no reason for being disabled. */}
        <Button variant="contained" onClick={handleConfirm}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { resolveCancellationInitiator, cancellationInitiatorDialog };
};

export default useCancellationInitiatorPrompt;
